import * as React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Pagination from '@amzn/awsui-components-console/pagination';
import Input from '@amzn/awsui-components-console/input';
import Button from '@amzn/awsui-components-console/button';
import Flashbar, { type FlashbarProps } from '@amzn/awsui-components-console/flashbar';
import PageLayoutWrapper from '../../components/PageLayoutWrapper/PageLayoutWrapper';
import ApplicationDetailCard from './components/ApplicationDetailCard';
import ApplicationDetailCardSkeleton from './components/ApplicationDetailCardSkeleton';
import ManageApplicationModal from './components/ManageApplicationModal/ManageApplicationModal';
import CreateApplicationModal from './components/CreateApplicationModal/CreateApplicationModal';
import { useApplications } from '../HomePage/hooks/useApplications';
import { portalService } from '../HomePage/services/portalService';
import './ApplicationsPage.scss';
import TextContent from '@amzn/awsui-components-console/text-content';
import { useFederate } from '../../shared/contexts/FederateContext';
import { isAdminUser } from '../../utils/roleUtils';

const ITEMS_PER_PAGE = 10;

const ApplicationsPage: React.FC = () => {
  const { userRoles } = useFederate();
  const isAdmin = isAdminUser(userRoles);
  const { applications, loading, error, refetch } = useApplications();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentPageIndex, setCurrentPageIndex] = React.useState(1);
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('search') || '');
  const [isManageModalVisible, setIsManageModalVisible] = React.useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = React.useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = React.useState<string>('');
  const [flashbarItems, setFlashbarItems] = React.useState<FlashbarProps.MessageDefinition[]>([]);

  // Filter applications based on search query
  const filteredApplications = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return applications;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return applications.filter((application) => application.useCaseId!.toLowerCase().includes(lowerQuery));
  }, [applications, searchQuery]);

  // Reset to page 1 when search query changes
  React.useEffect(() => {
    setCurrentPageIndex(1);
  }, [searchQuery]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredApplications.length / ITEMS_PER_PAGE);
  const startIndex = (currentPageIndex - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageApplications = filteredApplications.slice(startIndex, endIndex);

  // Flash message helper
  const showFlashMessage = (type: 'success' | 'error', message: string) => {
    const id = Date.now().toString();
    setFlashbarItems([
      {
        type,
        content: message,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems((items) => items.filter((item) => item.id !== id)),
        id,
      },
    ]);
  };

  const handleDataSourceClick = (collectionName: string) => {
    navigate(`/collections?search=${encodeURIComponent(collectionName)}`);
  };

  const handleManagementClick = (applicationId: string) => {
    setSelectedApplicationId(applicationId);
    setIsManageModalVisible(true);
  };

  const handleManageModalDismiss = () => {
    setIsManageModalVisible(false);
    setSelectedApplicationId('');
  };

  const handleCreateApplicationClick = () => {
    setIsCreateModalVisible(true);
  };

  const handleCreateModalDismiss = () => {
    setIsCreateModalVisible(false);
  };

  const handleCreateModalConfirm = async () => {
    try {
      // Frontend-only for now - just show success message
      setIsCreateModalVisible(false);
      showFlashMessage('success', 'Application onboarding request submitted successfully');
    } catch (error) {
      console.error('Failed to create application:', error);
      showFlashMessage('error', 'Failed to create application. Please try again.');
    }
  };

  const handleManageModalConfirm = async (selectedCollectionUrls: string[]) => {
    try {
      const currentUrls = (selectedApplication?.subscribedCollections || []).map((c) => c.collectionId!).filter(Boolean);

      // Find collections to subscribe to (new ones)
      const toSubscribe = selectedCollectionUrls.filter((url) => !currentUrls.includes(url));

      // Find collections to unsubscribe from (removed ones)
      const toUnsubscribe = currentUrls.filter((url) => !selectedCollectionUrls.includes(url));

      // Execute subscribe
      for (const collectionId of toSubscribe) {
        await portalService.subscribeToCollection({
          useCaseId: selectedApplicationId,
          collectionId: collectionId,
        });
      }

      // Execute unsubscribe
      for (const collectionId of toUnsubscribe) {
        await portalService.unsubscribeFromCollection({
          useCaseId: selectedApplicationId,
          collectionId: collectionId,
        });
      }

      setIsManageModalVisible(false);
      setSelectedApplicationId('');
      showFlashMessage('success', 'Application subscriptions updated successfully');

      // Refresh applications data
      await refetch();
    } catch (error) {
      console.error('Failed to update application subscriptions:', error);
      showFlashMessage('error', 'Failed to update application subscriptions. Please try again.');
    }
  };

  // Get selected application data
  const selectedApplication = React.useMemo(() => {
    return applications.find((application) => application.useCaseId === selectedApplicationId);
  }, [applications, selectedApplicationId]);

  if (error) {
    return (
      <PageLayoutWrapper
        heading="Applications"
        breadcrumbItems={[
          { text: 'Home', href: '/' },
          { text: 'Applications', href: '/applications' },
        ]}
      >
        <Box padding={{ vertical: 'l' }}>
          <div>Error: {error}</div>
        </Box>
      </PageLayoutWrapper>
    );
  }

  return (
    <PageLayoutWrapper
      heading="Applications"
      breadcrumbItems={[
        { text: 'Home', href: '/' },
        { text: 'Applications', href: '/applications' },
      ]}
      headerActions={
        isAdmin ? (
          <Button variant="primary" onClick={handleCreateApplicationClick}>
            Onboard new application
          </Button>
        ) : undefined
      }
    >
      <div className="applications-page">
        {/* Flashbar for success/error messages */}
        {flashbarItems.length > 0 && (
          <div className="applications-page__flashbar">
            <Flashbar items={flashbarItems} />
          </div>
        )}

        {/* Search and Pagination Header */}
        <div className="applications-page__header">
          <div className="applications-page__search">
            <Input
              type="search"
              placeholder="Search by application name"
              value={searchQuery}
              onChange={({ detail }) => setSearchQuery(detail.value)}
              clearAriaLabel="Clear search"
            />
          </div>
          <div className="applications-page__pagination">
            <Pagination
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              pagesCount={loading ? 1 : totalPages}
            />
          </div>
        </div>

        {/* Application Cards - Scrollable */}
        <div className="applications-page__content">
          <SpaceBetween size="l">
            {loading ? (
              <>
                {[...Array(ITEMS_PER_PAGE)].map((_, index) => (
                  <ApplicationDetailCardSkeleton key={index} />
                ))}
              </>
            ) : applications.length === 0 ? (
              <TextContent>
                <small>No applications found</small>
              </TextContent>
            ) : filteredApplications.length === 0 ? (
              <TextContent>
                <small>No applications match your search</small>
              </TextContent>
            ) : (
              <>
                {currentPageApplications.map((application) => (
                  <ApplicationDetailCard
                    key={application.useCaseId}
                    applicationId={application.useCaseId!}
                    createdAt={application.createdAt!}
                    subscriptionCount={application.subscribedCollections!.length}
                    lastUpdatedAt={application.lastUpdatedAt!}
                    lastUpdatedBy={application.lastUpdatedBy!}
                    dataSources={(application.subscribedCollections || []).map((collection) => ({
                      id: collection.collectionId || '',
                      label: collection.name || collection.collectionId || '',
                    }))}
                    onDataSourceClick={handleDataSourceClick}
                    onManagementClick={() => handleManagementClick(application.useCaseId!)}
                    isAdmin={isAdmin}
                  />
                ))}
              </>
            )}
          </SpaceBetween>
        </div>
      </div>

      {selectedApplication && (
        <ManageApplicationModal
          visible={isManageModalVisible}
          applicationId={selectedApplication.useCaseId!}
          applicationName={selectedApplication.useCaseId!}
          subscribedCollectionUrls={(selectedApplication.subscribedCollections || []).map((c) => c.collectionId!).filter(Boolean)}
          onDismiss={handleManageModalDismiss}
          onConfirm={handleManageModalConfirm}
        />
      )}

      <CreateApplicationModal visible={isCreateModalVisible} onDismiss={handleCreateModalDismiss} onConfirm={handleCreateModalConfirm} />
    </PageLayoutWrapper>
  );
};

export default ApplicationsPage;
